import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';

import { map, tap, catchError, switchMap, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { IFile } from '@care-portals/api-interfaces';

import * as FileActions from './file.actions';
import { APIService } from '../../services/api/api.service';

/**
 * Ensure tags array includes synthetic tags:
 * - "inbox" when the filename OR folder contains "inbox"
 * - "ids" when the filename matches ID/persona/selfie patterns
 */
function normalizeFileTags(file: { name?: string; folder?: string; tags?: string[] }): string[] {
  const tags = Array.isArray(file.tags) ? [...file.tags] : [];
  const name = (file.name || '').toLowerCase();
  const folder = (file.folder || '').toLowerCase();

  const addSyntheticTag = (tag: string) => {
    if (!tags.includes(tag)) tags.push(tag);
  };

  if (name.includes('inbox') || folder.includes('inbox')) {
    addSyntheticTag('inbox');
  }

  const idLikeNamePattern = /(?:^|[^A-Za-z0-9])(id|ids|govid|govids|passport|driver[_ -]?license|identity|persona|selfie)(?:$|[^A-Za-z0-9])/i;
  if (idLikeNamePattern.test(name)) {
    addSyntheticTag('ids');
  }

  return tags;
}

@Injectable()
export class FileEffects {
  loadFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileActions.loadFiles),
      switchMap(action =>
        this.apiService.getFiles(action.customerId, action.folder).pipe(
          // Map API response to IFile format, preserving folder information
          map((files: any[]) =>
            files.map(
              (file): IFile => ({
                name: file.name,
                folder: file.folder || '',
                createdAt: file.createdAt,
                updatedAt: file.updatedAt,
                tags: normalizeFileTags(file),
                loaded: false,
                encoding: '', // Will be set when file content is loaded
                buffer: null,
                mimetype: undefined
              })
            )
          ),
          map(files => FileActions.loadFilesSuccess({ files })),
          catchError(error => of(FileActions.loadFilesFailure({ error })))
        )
      )
    )
  );

  getFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileActions.getFile),
      mergeMap(action =>
        this.apiService
          .getFile(action.customerId, action.fileName, action.folder)
          .pipe(
            // Preserve folder information from the action since API response doesn't include it
            map(file => ({
              ...file,
              folder: action.folder || file.folder || ''
            })),
            map(file => FileActions.getFileSuccess({ file })),
            catchError(error => {
              console.error('Error', error);
              return of(FileActions.getFileFailure({
                error,
                fileName: action.fileName,
                folder: action.folder
              }));
            })
          )
      )
    )
  );

  uploadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileActions.uploadFile),
      mergeMap(({ customerId, file, folder, onSuccess }) =>
        this.apiService.uploadFile(customerId, file, folder).pipe(
          tap(resFile => onSuccess?.(resFile)),
          map(res =>
            FileActions.uploadFileSuccess({
              file: {
               
                ...file,
               
                name: res.name,
               
                loaded: true,
                folder: folder || ''
             ,
                // Ensure newly uploaded files behave like loaded ones
                // (synthetic tags + timestamps used across the UI).
                createdAt: new Date(),
                tags: Array.from(
                  new Set(
                    normalizeFileTags({
                      ...file,
                      name: res.name
                    } as { name?: string; folder?: string; tags?: string[] })
                  )
                )
              }
            })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', 'Failed to upload.');
            return of(FileActions.uploadFileFailure({ error }));
          })
        )
      )
    )
  );

  deleteFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileActions.deleteFile),
      mergeMap(action =>
        this.apiService
          .deleteFile(action.customerId, action.fileName, action.folder)
          .pipe(
            map(() => {
              action.onSuccess?.();
              return FileActions.deleteFileSuccess({
                fileName: action.fileName,
                folder: action.folder
              });
            }),
            catchError(error => {
              console.error('Error', error);
              this.notification.error('Error', 'Failed to delete file.');
              action.onFailure?.();
              return of(FileActions.deleteFileFailure({ error }));
            })
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private apiService: APIService,
    private notification: NzNotificationService
  ) { }
}
